/* ============================================================
 * SSC Forge Roll - v2 (3-spin session, multi-card chooser)
 *
 * Flow:
 *   intro    -> user enters email + accepts terms -> Start the Roll
 *   spinning -> reel decelerates onto a winner; card slides into
 *               the "won" rack; user clicks Spin again, up to 3 times
 *   chooser  -> 3 won cards laid out with checkboxes + discounts;
 *               user picks any/all/none + clicks Add to cart
 *   cooldown -> shown if user already played in last 24h
 *
 * Discount handling:
 *   Shopify caps a cart at ONE discount code. So when the user
 *   selects multiple winning cards, we add them all to cart and
 *   apply the highest-percentage code via /discount/CODE (which
 *   sets a session-level discount that auto-applies at checkout).
 *   The remaining codes are still saved to localStorage so the
 *   user can use them on future orders (single-use, 90-day expiry).
 *
 * Data sources:
 *   .card-wrap-compareable[data-*] tiles - product catalogue
 *   #ssc-card-mounts[data-product-id] - full card markup for clones
 *   ssc-forge-roll-codes.json - code pool per tier bucket
 * ============================================================ */
(function () {
  'use strict';

  /* ── Config ─────────────────────────────────────────────────────── */
  const SPINS_PER_SESSION = 3;
  const TIER_PROBABILITIES = {
    uncommon:  0.35,
    rare:      0.35,
    magic:     0.20,
    legendary: 0.08,
    unique:    0.02,
  };
  const TIER_DISCOUNTS = {
    uncommon:  5,
    rare:      7,
    magic:     10,
    legendary: 12,
    unique:    15,
  };
  const TIER_BUCKET_MEMBERS = {
    uncommon:  ['uncommon'],
    rare:      ['rare'],
    magic:     ['magic'],
    legendary: ['legendary'],
    unique:    ['unique', 'mythic'],
  };

  const COOLDOWN_MS = 24 * 60 * 60 * 1000;
  const SPIN_DURATION_MS = 7000;
  const REEL_CARD_WIDTH_PX = 180;

  const COOLDOWN_KEY = 'ssc_forge_roll_last_session';
  const CODE_IDX_PFX = 'ssc_forge_roll_code_idx_';
  const SAVED_CODES_KEY = 'ssc_forge_roll_saved_codes';

  /* ── DOM handles ────────────────────────────────────────────────── */
  const trigger = document.querySelector('[data-ssc-forge-roll-trigger]');
  const modal   = document.getElementById('ssc-forge-roll-modal');
  if (!trigger || !modal) return;

  const triggerLabel = trigger.querySelector('[data-ssc-forge-roll-trigger-label]');
  const triggerSub   = trigger.querySelector('[data-ssc-forge-roll-trigger-sub]');

  const form          = modal.querySelector('[data-ssc-forge-roll-form]');
  const emailInput    = modal.querySelector('[data-ssc-forge-roll-email]');
  const ageCheckbox   = modal.querySelector('[data-ssc-forge-roll-age]');
  const startBtn      = modal.querySelector('[data-ssc-forge-roll-start]');
  const spinBtn       = modal.querySelector('[data-ssc-forge-roll-spin]');
  const spinNumEl     = modal.querySelector('[data-ssc-forge-roll-spin-num]');
  const spinSubEl     = modal.querySelector('[data-ssc-forge-roll-spin-sub]');
  const reelStrip     = modal.querySelector('[data-ssc-forge-roll-strip]');
  const rackEl        = modal.querySelector('[data-ssc-forge-roll-rack]');
  const rackListEl    = modal.querySelector('[data-ssc-forge-roll-rack-list]');
  const chooserListEl = modal.querySelector('[data-ssc-forge-roll-chooser-list]');
  const chooserCount  = modal.querySelector('[data-ssc-forge-roll-chooser-count]');
  const chooserSaving = modal.querySelector('[data-ssc-forge-roll-chooser-saving]');
  const addBtn        = modal.querySelector('[data-ssc-forge-roll-add]');
  const cooldownRem   = modal.querySelector('[data-ssc-forge-roll-cooldown-remaining]');

  const codesUrl = modal.dataset.codesUrl || '/assets/ssc-forge-roll-codes.json';
  // When set (theme setting forge_roll_worker_url), codes are claimed
  // server-side from the forge-roll-claim worker instead of the public
  // JSON, so the full list is never exposed and two players can't get
  // the same code. Empty string -> legacy JSON behaviour (fallback).
  const workerUrl = (modal.dataset.workerUrl || '').replace(/\/$/, '');

  /* ── State ──────────────────────────────────────────────────────── */
  let codePool        = null;
  let spinNumber      = 0;     // 0 before first spin, 1-3 mid-session
  let wonItems        = [];    // { product, bucket, code, discountPct }
  let spinning        = false;
  let focusReturnTarget = null;
  let tickRAF         = null;
  let sessionEmail    = '';    // captured at session start, used for /notify

  /* ── Web Audio FX (sound design unchanged from v1) ────────────────── */
  const audio = (() => {
    let ctx = null;
    function ensureCtx() {
      if (ctx) {
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        return ctx;
      }
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        ctx = new Ctx();
      } catch (e) { return null; }
      return ctx;
    }
    function playTick() {
      const c = ensureCtx(); if (!c) return;
      const now = c.currentTime;
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(1800, now);
      osc.frequency.exponentialRampToValueAtTime(900, now + 0.04);
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.0005, now + 0.05);
      osc.connect(gain).connect(c.destination);
      osc.start(now);
      osc.stop(now + 0.06);
    }
    function playOpen() {
      const c = ensureCtx(); if (!c) return;
      const now = c.currentTime;
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(700, now);
      osc.frequency.exponentialRampToValueAtTime(150, now + 0.4);
      gain.gain.setValueAtTime(0.0005, now);
      gain.gain.linearRampToValueAtTime(0.10, now + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0005, now + 0.45);
      osc.connect(gain).connect(c.destination);
      osc.start(now);
      osc.stop(now + 0.5);
    }
    function playWin() {
      const c = ensureCtx(); if (!c) return;
      const now = c.currentTime;
      const freqs = [523.25, 659.25, 783.99];
      freqs.forEach((freq, i) => {
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const startT = now + i * 0.05;
        gain.gain.setValueAtTime(0.0005, startT);
        gain.gain.linearRampToValueAtTime(0.13, startT + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.0005, startT + 1.4);
        osc.connect(gain).connect(c.destination);
        osc.start(startT);
        osc.stop(startT + 1.5);
      });
      const bell = c.createOscillator();
      const bellG = c.createGain();
      bell.type = 'triangle';
      bell.frequency.value = 1567.98;
      bellG.gain.setValueAtTime(0.0005, now);
      bellG.gain.linearRampToValueAtTime(0.05, now + 0.02);
      bellG.gain.exponentialRampToValueAtTime(0.0005, now + 0.7);
      bell.connect(bellG).connect(c.destination);
      bell.start(now);
      bell.stop(now + 0.75);
    }
    return { playTick, playOpen, playWin, ensureCtx };
  })();

  /* ── Product catalogue ──────────────────────────────────────────── */
  function collectProducts() {
    const out = [];
    document.querySelectorAll('.card-wrap-compareable').forEach(wrap => {
      const input = wrap.querySelector('[data-ssc-compare-input]');
      if (!input) return;
      const tier = input.dataset.productTier || '';
      out.push({
        id:        input.dataset.productId        || '',
        handle:    input.dataset.productHandle    || '',
        title:     input.dataset.productTitle     || '',
        url:       input.dataset.productUrl       || '',
        image:     input.dataset.productImage     || '',
        variantId: input.dataset.productVariantId || '',
        available: input.dataset.productAvailable === 'true',
        tier:      tier,
      });
    });
    return out;
  }
  function productsForBucket(allProducts, bucket) {
    const tiers = TIER_BUCKET_MEMBERS[bucket] || [];
    return allProducts.filter(p => tiers.includes(p.tier) && p.available);
  }

  /* ── Weighted random pick ───────────────────────────────────────── */
  function pickBucket() {
    const r = Math.random();
    let acc = 0;
    for (const [bucket, prob] of Object.entries(TIER_PROBABILITIES)) {
      acc += prob;
      if (r <= acc) return bucket;
    }
    return 'uncommon';
  }
  function pickWinner(allProducts, exclude) {
    const order = ['uncommon', 'rare', 'magic', 'legendary', 'unique'];
    const startBucket = pickBucket();
    const startIdx = order.indexOf(startBucket);

    const tryBucket = (idx) => {
      if (idx < 0 || idx >= order.length) return null;
      const bucket = order[idx];
      let pool = productsForBucket(allProducts, bucket);
      // Exclude already-won product IDs so the same blade can't come up twice
      pool = pool.filter(p => !exclude.includes(p.id));
      if (!pool.length) return null;
      return { product: pool[Math.floor(Math.random() * pool.length)], bucket };
    };
    let pick = tryBucket(startIdx);
    if (pick) return pick;
    for (let dist = 1; dist < order.length; dist++) {
      pick = tryBucket(startIdx - dist) || tryBucket(startIdx + dist);
      if (pick) return pick;
    }
    return null;
  }

  /* ── Code pool ──────────────────────────────────────────────────── */
  async function loadCodePool() {
    if (codePool) return codePool;
    try {
      const res = await fetch(codesUrl, { credentials: 'omit' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      codePool = await res.json();
    } catch (e) {
      console.warn('[forge-roll] code pool fetch failed, using placeholders', e);
      codePool = {
        uncommon:  ['FORGE-UC-PLACEHOLDER'],
        rare:      ['FORGE-RA-PLACEHOLDER'],
        magic:     ['FORGE-MA-PLACEHOLDER'],
        legendary: ['FORGE-LE-PLACEHOLDER'],
        unique:    ['FORGE-UN-PLACEHOLDER'],
      };
    }
    return codePool;
  }
  // Acquire one code for a tier. Worker path (preferred) pops an
  // unissued code server-side; on any failure we fall back to the
  // local JSON pool so a worker outage degrades instead of breaking.
  async function claimCode(bucket) {
    if (workerUrl) {
      try {
        const res = await fetch(workerUrl + '/claim', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tier: bucket }),
          credentials: 'omit',
        });
        if (res.ok) {
          const data = await res.json();
          if (data && data.code) return data.code;
          // pool-empty -> fall through to JSON fallback below
        }
      } catch (e) {
        console.warn('[forge-roll] worker claim failed, falling back to JSON', e);
      }
    }
    await loadCodePool();
    return popCode(bucket);
  }
  function popCode(bucket) {
    const pool = (codePool && codePool[bucket]) || [];
    if (!pool.length) return null;
    const idxKey = CODE_IDX_PFX + bucket;
    let idx = parseInt(localStorage.getItem(idxKey) || '0', 10);
    if (idx >= pool.length) idx = 0;
    const code = pool[idx];
    try { localStorage.setItem(idxKey, String(idx + 1)); } catch (e) {}
    return code;
  }
  // Fire-and-forget email of the won codes via the worker's /notify.
  // No-ops without a worker URL or email. Never blocks the UI.
  function sendNotify() {
    if (!workerUrl || !sessionEmail || !wonItems.length) return;
    const payload = {
      email: sessionEmail,
      codes: wonItems
        .filter(w => w.code && w.code !== '(unavailable)')
        .map(w => ({
          code: w.code,
          title: w.product && w.product.title,
          url: w.product && w.product.url,
          discountPct: w.discountPct,
          tier: w.bucket,
        })),
    };
    if (!payload.codes.length) return;
    try {
      fetch(workerUrl + '/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
        keepalive: true,
      }).catch(() => {});
    } catch (e) {}
  }
  function saveCodes(items) {
    try {
      const existing = JSON.parse(localStorage.getItem(SAVED_CODES_KEY) || '[]');
      const entry = {
        ts: Date.now(),
        items: items.map(i => ({
          title: i.product.title,
          url: i.product.url,
          code: i.code,
          discountPct: i.discountPct,
          tier: i.bucket,
        })),
      };
      existing.push(entry);
      // Keep only the last 10 sessions to avoid bloat
      while (existing.length > 10) existing.shift();
      localStorage.setItem(SAVED_CODES_KEY, JSON.stringify(existing));
    } catch (e) {}
  }

  /* ── Cooldown ───────────────────────────────────────────────────── */
  function getCooldownMsRemaining() {
    const last = parseInt(localStorage.getItem(COOLDOWN_KEY) || '0', 10);
    if (!last) return 0;
    const remaining = COOLDOWN_MS - (Date.now() - last);
    return remaining > 0 ? remaining : 0;
  }
  function setSessionDone() {
    try { localStorage.setItem(COOLDOWN_KEY, String(Date.now())); } catch (e) {}
  }
  function formatRemaining(ms) {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h > 0) return h + 'h ' + m + 'm';
    return m + 'm';
  }
  function refreshTriggerLabel() {
    const rem = getCooldownMsRemaining();
    if (rem > 0) {
      if (triggerLabel) triggerLabel.textContent = 'Come back in ' + formatRemaining(rem);
      if (triggerSub)   triggerSub.textContent   = 'Forge is cooling';
      trigger.classList.add('is-cooling');
    } else {
      if (triggerLabel) triggerLabel.textContent = 'Forge Roll';
      if (triggerSub)   triggerSub.textContent   = 'Free spin · discount inside';
      trigger.classList.remove('is-cooling');
    }
  }

  /* ── Modal open/close + pane switching ──────────────────────────── */
  function setPane(state) {
    modal.dataset.state = state;
    modal.querySelectorAll('[data-ssc-forge-roll-pane]').forEach(p => {
      p.hidden = p.dataset.sscForgeRollPane !== state;
    });
  }
  function openModal() {
    focusReturnTarget = document.activeElement;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    const rem = getCooldownMsRemaining();
    if (rem > 0) {
      if (cooldownRem) cooldownRem.textContent = formatRemaining(rem);
      setPane('cooldown');
    } else {
      // Reset session state
      spinNumber = 0;
      wonItems = [];
      if (rackEl) rackEl.hidden = true;
      if (rackListEl) rackListEl.innerHTML = '';
      setPane('intro');
      if (emailInput) setTimeout(() => emailInput.focus(), 50);
    }
    document.addEventListener('keydown', handleKeyboard);
  }
  function closeModal() {
    modal.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', handleKeyboard);
    if (focusReturnTarget && typeof focusReturnTarget.focus === 'function') {
      try { focusReturnTarget.focus(); } catch (e) {}
    }
  }
  function handleKeyboard(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
    }
  }
  trigger.addEventListener('click', () => {
    audio.ensureCtx();
    openModal();
  });
  modal.querySelectorAll('[data-ssc-forge-roll-dismiss]').forEach(el => {
    el.addEventListener('click', closeModal);
  });
  // Deep link from the nav "Play" menu (/collections/knives#forge-roll):
  // open the modal straight away. Audio stays off until the first tap.
  if (window.location.hash === '#forge-roll') {
    setTimeout(openModal, 300);
  }

  /* ── Form validation ────────────────────────────────────────────── */
  function syncFormValidity() {
    if (!form || !startBtn) return;
    const emailOk = emailInput && emailInput.value && /\S+@\S+\.\S+/.test(emailInput.value);
    const ageOk   = ageCheckbox && ageCheckbox.checked;
    startBtn.disabled = !(emailOk && ageOk);
  }
  if (emailInput)  emailInput.addEventListener('input', syncFormValidity);
  if (ageCheckbox) ageCheckbox.addEventListener('change', syncFormValidity);

  /* ── Start session ──────────────────────────────────────────────── */
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (startBtn && startBtn.disabled) return;
      audio.ensureCtx();

      const rem = getCooldownMsRemaining();
      if (rem > 0) {
        if (cooldownRem) cooldownRem.textContent = formatRemaining(rem);
        setPane('cooldown');
        return;
      }

      // Capture the email for the post-session /notify send.
      sessionEmail = (emailInput && emailInput.value || '').trim();

      // Preload the JSON pool (fallback path / when no worker is set).
      loadCodePool();

      spinNumber = 0;
      wonItems = [];
      if (rackEl) rackEl.hidden = true;
      if (rackListEl) rackListEl.innerHTML = '';

      setPane('spinning');
      updateSpinHeader();
    });
  }

  function updateSpinHeader() {
    if (spinNumEl) spinNumEl.textContent = String(spinNumber + 1);
    if (spinSubEl) {
      if (spinNumber === 0) spinSubEl.textContent = 'Hit Spin when you\'re ready.';
      else spinSubEl.textContent = 'You won ' + wonItems.length + ' of 3 - one more chance for each Spin.';
    }
    if (spinBtn) {
      spinBtn.disabled = false;
      spinBtn.textContent = spinNumber === 0 ? 'Spin' : 'Spin again';
    }
  }

  /* ── Spin button handler ────────────────────────────────────────── */
  if (spinBtn) {
    spinBtn.addEventListener('click', async () => {
      if (spinning) return;
      if (wonItems.length >= SPINS_PER_SESSION) return;
      spinning = true;
      spinBtn.disabled = true;
      audio.ensureCtx();
      // Cooldown starts at the FIRST spin, not after the 3rd -
      // closing the modal after spin 1-2 used to allow unlimited
      // replays (each abandoned run still consumed pool codes).
      setSessionDone();
      await spinOnce();
      spinning = false;

      if (wonItems.length >= SPINS_PER_SESSION) {
        // Session complete - transition to chooser, record cooldown
        setSessionDone();
        saveCodes(wonItems);
        sendNotify();          // email the codes (worker + email required)
        buildChooser();
        setPane('chooser');
        document.dispatchEvent(new CustomEvent('forge:session-complete', { detail: { buckets: wonItems.map(w => w.bucket) } }));
        refreshTriggerLabel();
      } else {
        spinNumber = wonItems.length;
        updateSpinHeader();
      }
    });
  }

  async function spinOnce() {
    const allProducts = collectProducts();
    if (!allProducts.length) {
      console.warn('[forge-roll] no products available to spin');
      return;
    }
    const exclude = wonItems.map(w => w.product.id);
    const winner = pickWinner(allProducts, exclude);
    if (!winner) {
      console.warn('[forge-roll] no eligible products left');
      return;
    }

    const code = await claimCode(winner.bucket);
    const discountPct = TIER_DISCOUNTS[winner.bucket];

    populateReel(allProducts, winner.product);
    audio.playOpen();
    startTickLoop();
    await animateReel();
    stopTickLoop();
    audio.playWin();

    const won = {
      product: winner.product,
      bucket: winner.bucket,
      code: code || '(unavailable)',
      discountPct: discountPct,
    };
    wonItems.push(won);
    appendToRack(won);
  }

  /* ── Tick loop ──────────────────────────────────────────────────── */
  function startTickLoop() {
    if (!reelStrip) return;
    const cards = reelStrip.querySelectorAll('.ssc-forge-roll-reel-card');
    const stride = cards.length >= 2
      ? (cards[1].offsetLeft - cards[0].offsetLeft)
      : (cards[0] && cards[0].offsetWidth) || REEL_CARD_WIDTH_PX;
    let lastTickIdx = -1;
    const tickLoop = () => {
      if (!reelStrip) { tickRAF = null; return; }
      const transform = window.getComputedStyle(reelStrip).transform;
      if (transform && transform !== 'none') {
        const m = transform.match(/matrix.*\(([^)]+)\)/);
        if (m) {
          const values = m[1].split(',').map(parseFloat);
          const tx = values.length >= 16 ? values[12] : values[4];
          const absTx = Math.abs(tx || 0);
          const currentTickIdx = Math.floor(absTx / stride);
          if (currentTickIdx > lastTickIdx) {
            audio.playTick();
            lastTickIdx = currentTickIdx;
          }
        }
      }
      tickRAF = requestAnimationFrame(tickLoop);
    };
    tickLoop();
  }
  function stopTickLoop() {
    if (tickRAF) {
      cancelAnimationFrame(tickRAF);
      tickRAF = null;
    }
  }

  /* ── Reel ───────────────────────────────────────────────────────── */
  function populateReel(allProducts, winnerProduct) {
    if (!reelStrip) return;
    reelStrip.innerHTML = '';
    const shuffled = [...allProducts].sort(() => Math.random() - 0.5);
    const repeats = Math.max(1, Math.ceil(40 / Math.max(1, shuffled.length)));
    const sequence = [];
    for (let i = 0; i < repeats; i++) sequence.push(...shuffled);
    const winnerIdx = sequence.length - 8;
    sequence[winnerIdx] = winnerProduct;
    sequence.forEach((p, i) => {
      const card = document.createElement('div');
      card.className = 'ssc-forge-roll-reel-card tier-' + (p.tier || 'common');
      if (i === winnerIdx) card.dataset.sscForgeRollWinner = '1';
      const img = document.createElement('img');
      img.src = p.image;
      img.alt = '';
      img.loading = 'lazy';
      card.appendChild(img);
      const name = document.createElement('span');
      name.className = 'ssc-forge-roll-reel-card-name';
      name.textContent = p.title;
      card.appendChild(name);
      reelStrip.appendChild(card);
    });
    reelStrip.dataset.winnerIdx = String(winnerIdx);
  }

  function animateReel() {
    return new Promise(resolve => {
      const reel = modal.querySelector('[data-ssc-forge-roll-reel]');
      if (!reel || !reelStrip) { resolve(); return; }
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Snap reset (no transition) so subsequent spins start from 0
      reelStrip.style.transition = 'none';
      reelStrip.style.transform = 'translateX(0)';
      void reelStrip.offsetWidth;

      const winnerCard = reelStrip.querySelector('[data-ssc-forge-roll-winner="1"]');
      if (!winnerCard) { resolve(); return; }
      const reelWidth = reel.clientWidth;
      const targetX = (reelWidth / 2) - (winnerCard.offsetLeft + winnerCard.offsetWidth / 2);
      const jitter  = (Math.random() - 0.5) * (winnerCard.offsetWidth * 0.2);

      if (reduced) {
        reelStrip.style.transform = 'translateX(' + (targetX + jitter) + 'px)';
        setTimeout(resolve, 200);
        return;
      }
      reelStrip.style.transition = 'transform ' + SPIN_DURATION_MS + 'ms cubic-bezier(0.15, 0.85, 0.25, 1)';
      reelStrip.style.transform = 'translateX(' + (targetX + jitter) + 'px)';
      setTimeout(resolve, SPIN_DURATION_MS + 50);
    });
  }

  /* ── Rack (running list of won cards under the reel) ────────────── */
  function appendToRack(won) {
    if (!rackEl || !rackListEl) return;
    rackEl.hidden = false;

    const chip = document.createElement('div');
    chip.className = 'ssc-forge-roll-rack-card tier-' + (won.product.tier || 'common');
    chip.innerHTML =
      '<div class="ssc-forge-roll-rack-card-art">' +
        '<img src="' + escapeHtml(won.product.image) + '" alt="" loading="lazy">' +
      '</div>' +
      '<div class="ssc-forge-roll-rack-card-info">' +
        '<div class="ssc-forge-roll-rack-card-title">' + escapeHtml(won.product.title) + '</div>' +
        '<div class="ssc-forge-roll-rack-card-tier">' + escapeHtml(capitalize(won.bucket)) + ' &middot; ' + won.discountPct + '% off</div>' +
      '</div>';
    chip.classList.add('is-arriving');
    setTimeout(() => chip.classList.remove('is-arriving'), 1200);
    rackListEl.appendChild(chip);
  }

  /* ── Chooser pane ───────────────────────────────────────────────── */
  function buildChooser() {
    if (!chooserListEl) return;
    chooserListEl.innerHTML = '';
    wonItems.forEach((won, idx) => {
      const row = document.createElement('label');
      row.className = 'ssc-forge-roll-chooser-row tier-' + (won.product.tier || 'common');
      row.dataset.idx = String(idx);
      row.innerHTML =
        '<input type="checkbox" class="ssc-forge-roll-chooser-check" data-ssc-forge-roll-chooser-check checked>' +
        '<span class="ssc-forge-roll-chooser-box" aria-hidden="true">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
            '<polyline points="20 6 9 17 4 12"/>' +
          '</svg>' +
        '</span>' +
        '<div class="ssc-forge-roll-chooser-card">' +
          '<div class="ssc-forge-roll-chooser-card-art">' +
            '<img src="' + escapeHtml(won.product.image) + '" alt="" loading="lazy">' +
          '</div>' +
          '<div class="ssc-forge-roll-chooser-card-info">' +
            '<div class="ssc-forge-roll-chooser-card-tier">' + escapeHtml(capitalize(won.bucket)) + ' &middot; ' + won.discountPct + '% off</div>' +
            '<div class="ssc-forge-roll-chooser-card-title">' + escapeHtml(won.product.title) + '</div>' +
            '<div class="ssc-forge-roll-chooser-card-code">' +
              '<code>' + escapeHtml(won.code) + '</code>' +
            '</div>' +
          '</div>' +
        '</div>';
      chooserListEl.appendChild(row);
    });

    // Wire change handlers
    chooserListEl.querySelectorAll('[data-ssc-forge-roll-chooser-check]').forEach(cb => {
      cb.addEventListener('change', updateChooserSummary);
    });

    updateChooserSummary();
  }

  function updateChooserSummary() {
    const checked = Array.from(chooserListEl.querySelectorAll('[data-ssc-forge-roll-chooser-check]:checked'));
    if (chooserCount) {
      chooserCount.textContent = checked.length + ' selected';
    }
    if (chooserSaving) {
      const best = checked.reduce((m, cb) => {
        const idx = parseInt(cb.closest('.ssc-forge-roll-chooser-row').dataset.idx, 10);
        return Math.max(m, wonItems[idx] ? wonItems[idx].discountPct : 0);
      }, 0);
      chooserSaving.textContent = best > 0 ? ('Best code: ' + best + '% off auto-applied at checkout') : '';
    }
    if (addBtn) {
      addBtn.disabled = checked.length === 0;
    }
  }

  /* ── Add selected to cart ──────────────────────────────────────────
     Shopify caps a cart at ONE discount code, so we add every selected
     product via /cart/add.js, then redirect to /discount/<BEST_CODE>
     to set the best discount as a session-level discount that applies
     at checkout. The unused codes are already saved to localStorage
     (and emailed - integration TODO) so the user can use them later.
  ──────────────────────────────────────────────────────────────────── */
  if (addBtn) {
    addBtn.addEventListener('click', async () => {
      if (addBtn.disabled) return;
      const checked = Array.from(chooserListEl.querySelectorAll('[data-ssc-forge-roll-chooser-check]:checked'));
      if (!checked.length) return;

      addBtn.disabled = true;
      const orig = addBtn.textContent;
      addBtn.textContent = 'Adding to cart...';

      const selectedItems = checked.map(cb => {
        const idx = parseInt(cb.closest('.ssc-forge-roll-chooser-row').dataset.idx, 10);
        return wonItems[idx];
      }).filter(Boolean);

      // Pick the highest-discount code as the auto-applied one
      const best = selectedItems.reduce((m, w) => (w.discountPct > m.discountPct ? w : m), selectedItems[0]);

      try {
        // Batch /cart/add.js with all selected variant IDs
        const items = selectedItems
          .filter(w => w.product.variantId)
          .map(w => ({ id: parseInt(w.product.variantId, 10), quantity: 1 }));

        if (items.length === 0) {
          // Fall back: redirect to first product
          window.location.href = selectedItems[0].product.url;
          return;
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items }),
        });
        if (!res.ok) throw new Error('cart/add.js HTTP ' + res.status);

        // Set the best discount via /discount/CODE then redirect to /cart
        if (best && best.code && best.code !== '(unavailable)') {
          window.location.href = '/discount/' + encodeURIComponent(best.code) + '?redirect=/cart';
        } else {
          window.location.href = '/cart';
        }
      } catch (e) {
        console.error('[forge-roll] add to cart failed', e);
        addBtn.disabled = false;
        addBtn.textContent = orig;
      }
    });
  }

  /* ── Utils ──────────────────────────────────────────────────────── */
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }
  function capitalize(s) {
    if (s === 'magic') return 'Epic';
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }

  /* ── Init ───────────────────────────────────────────────────────── */
  syncFormValidity();
  refreshTriggerLabel();
  setInterval(refreshTriggerLabel, 60000);
})();
